import { PlatformLocation } from '@angular/common';
import { Injectable } from '@angular/core';
import { KnownRequestInstrumentationEvents } from '@office-start/telemetry-types';
import { Observable } from 'rxjs';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { ErrorId } from '../../error-handler/error-id';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpPromise } from '../instrumentation/instrumented-http.service';
import { ErrorDialogErrorCategory } from '../models/error-dialog.model';
import { FileType } from '../models/file-item.model';
import { GraphError } from '../models/graph.model';
import { GraphGetOneNoteNotebooksResponse, GraphOneNoteNotebook } from '../models/onenote-graph.model';
import { OperationStream } from '../models/operation-stream.model';
import { ContextService } from '../shared-context/context.service';
import { ObservableHelper } from '../utilities/observable-helper.utility';
import { AccessTokenService } from './access-token.service';
import { GraphService } from './graph.service';
import { OfficeHomeApiConfigService } from './officehome-api-config.service';
import { SchedulerService } from './scheduler.service';
import { XhrServiceError } from './xhr.service';

export const enum GetNotebookFetchStrategy {
    StaleWhileRevalidate = 'stale-while-revalidate',
    CacheFirst = 'cache-first-network-onmiss'
}

/**
 * Service that makes requests to Graph api to interface with onenote.
 */
@Injectable({
    providedIn: 'root'
})
export class OneNoteGraphService extends GraphService {
    private static readonly myOneNoteEndpoint = 'me/onenote/';

    constructor(
        accessTokenService: AccessTokenService,
        instrumentationService: InstrumentationService,
        instrumentedHttp: InstrumentedHttp,
        contextService: ContextService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        platformLocation: PlatformLocation,
        globalErrorHandler: OfficeHomePageErrorHandler,
        scheduler: SchedulerService
    ) {
        super(accessTokenService, instrumentationService, instrumentedHttp, contextService, officeHomeApiConfigService, platformLocation, globalErrorHandler, scheduler);
    }

    public createNewNotebook(displayName: string): Promise<GraphOneNoteNotebook> {
        const url = `${OneNoteGraphService.myOneNoteEndpoint}notebooks`;
        const postBody = {
            'displayName': displayName
        };

        return new Promise<GraphOneNoteNotebook>((resolve, reject) => {
            this.postGraph<GraphOneNoteNotebook | GraphError>(
                KnownInstrumentationEvents.OneNoteGraphCreateNoteBookRequest,
                KnownRequestInstrumentationEvents.OneNoteGraphCreateNoteBookRequest,
                url,
                postBody,
                (promise: InstrumentedHttpPromise<GraphOneNoteNotebook | GraphError>) => {
                    promise
                        .instrument((result, params) => {
                            params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
                        })
                        .then(response => {
                            const item = response.data as GraphOneNoteNotebook;
                            if (item && item.links && item.links.oneNoteWebUrl) {
                                resolve(item);
                            } else {
                                reject(new XhrServiceError(response, 'Could not parse notebook item from Graph response'));
                            }
                        })
                        .catch(error => {
                            this.globalErrorHandler.handleError(error, ErrorId.vFlcr);
                        });
                },
                (xhrError: XhrServiceError<GraphOneNoteNotebook | GraphError>) => {
                    if (xhrError && xhrError.httpResponse) {
                        if (xhrError.httpResponse.data) {
                            reject({ error: { code: `${xhrError.httpResponse.status}`, data: `${xhrError.httpResponse.data}` } });
                        } else {
                            reject({ error: { code: `${xhrError.httpResponse.status}` } });
                        }
                    } else {
                        reject();
                    }
                },
                null,
                null,
                null,
                null,
                null,
                true,
                { category: ErrorDialogErrorCategory.CreateFile, fileType: FileType.OneNote});
        });
    }

    public getNoteBooks(fetchStrategy: FetchStrategy | GetNotebookFetchStrategy): OperationStream<GraphGetOneNoteNotebooksResponse> {
        if (fetchStrategy === GetNotebookFetchStrategy.StaleWhileRevalidate) {
            const notebookDataFromCache = this.fetchNoteBooks(FetchStrategy.CacheOnly);
            const notebookDataFromNetwork = this.fetchNoteBooks(FetchStrategy.NetworkFirst);
            const notebookDataStream = ObservableHelper.createResultsWithCacheObservable(
                notebookDataFromCache,
                notebookDataFromNetwork
            );
            return notebookDataStream;
        } else if (fetchStrategy === GetNotebookFetchStrategy.CacheFirst) {
            return {
                results: Observable.create(observer => {
                    this.fetchNoteBooks(FetchStrategy.CacheOnly).then(response => {
                        observer.next(response);
                        observer.complete();
                    }).catch(() => {
                        this.fetchNoteBooks(FetchStrategy.NetworkFirst).then(response => {
                            observer.next(response);
                            observer.complete();
                        });
                    });
                }),
                initialCompletion: null
            };
        } else {
            return {
                results: Observable.create(observer => {
                    this.fetchNoteBooks(fetchStrategy).then(response => {
                        observer.next(response);
                        observer.complete();
                    });
                }),
                initialCompletion: null
            };
        }
    }

    public fetchNoteBooks(fetchStrategy: FetchStrategy): Promise<GraphGetOneNoteNotebooksResponse> {
        const url = `${OneNoteGraphService.myOneNoteEndpoint}notebooks?orderby=lastModifiedTime%20desc`;
        return this.getGraph<GraphGetOneNoteNotebooksResponse>(
            KnownInstrumentationEvents.OneNoteGraphGetNotebooksRequest,
            KnownRequestInstrumentationEvents.OneNoteGraphGetNotebooksRequest,
            url,
            fetchStrategy,
            (result, params) => {
                params[InstrumentationParam.RequestId] = this.getRequestIdFromResponse(result);
            }
        ).then(response => Object.assign(response.data, { 'fromCache': response.fromCache }));
    }

    protected handleQueryParam(paramNameInLowerCase: string, value: string | Array<string>) {
            if (paramNameInLowerCase === 'mockgetnotebooks' && this.mockRequestHeader) {
            this.requestConfig.headers.set(this.mockRequestHeader, value);
        }
    }
}
